import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X, Search } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ManufacturerDropdownProps {
  manufacturers: string[];
  selected: string[];
  counts?: Record<string, number>;
  onChange: (selected: string[]) => void;
}

export const ManufacturerDropdown = ({
  manufacturers,
  selected,
  counts = {},
  onChange
}: ManufacturerDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredManufacturers = useMemo(() => {
    return manufacturers
      .filter(mfg => mfg.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }, [manufacturers, searchQuery]);

  const handleToggleManufacturer = (manufacturer: string) => {
    if (selected.includes(manufacturer)) {
      onChange(selected.filter(m => m !== manufacturer));
    } else {
      onChange([...selected, manufacturer]);
    }
  };

  const handleSelectAll = () => {
    onChange(filteredManufacturers);
  };

  const handleClearAll = () => {
    onChange([]);
    setOpen(false);
  };

  const handleRemoveManufacturer = (manufacturer: string) => {
    onChange(selected.filter(m => m !== manufacturer));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="min-w-[200px] justify-between">
              Select Manufacturers
              {selected.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selected.length}
                </Badge>
              )}
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search manufacturers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="flex-1"
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="flex-1"
                >
                  Clear All
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="p-3 space-y-2">
                {filteredManufacturers.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No manufacturers found
                  </div>
                ) : (
                  filteredManufacturers.map((manufacturer) => (
                    <div
                      key={manufacturer}
                      className="flex items-center space-x-2 hover:bg-accent rounded-md p-2 cursor-pointer"
                      onClick={() => handleToggleManufacturer(manufacturer)}
                    >
                      <Checkbox
                        checked={selected.includes(manufacturer)}
                        onCheckedChange={() => handleToggleManufacturer(manufacturer)}
                      />
                      <label className="flex-1 text-sm cursor-pointer">
                        {manufacturer}
                      </label>
                      {counts[manufacturer] !== undefined && counts[manufacturer] > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {counts[manufacturer]}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>

        {selected.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="text-muted-foreground"
          >
            Clear Manufacturers
          </Button>
        )}
      </div>

      {/* Selected Manufacturer Badges */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((manufacturer) => (
            <Badge
              key={manufacturer}
              variant="secondary"
              className="pl-3 pr-1 py-1 cursor-pointer hover:bg-secondary/80"
            >
              {manufacturer}
              {counts[manufacturer] !== undefined && counts[manufacturer] > 0 && (
                <span className="ml-1 text-xs opacity-70">({counts[manufacturer]})</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0.5 ml-1 hover:bg-transparent"
                aria-label={`Remove ${manufacturer}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveManufacturer(manufacturer);
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
